import React, {useCallback, useState} from 'react';

import {assert} from 'Asserts';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import isProcedureVersionPoW from 'procedures/design/utils/isProcedureVersionPoW';
import {ProcedureRunPlanConditionREST, updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import {deriveConfirmationMethodByParamVersionId} from 'procedures/planning/buildFullExperimentPlan';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as modals from 'util/Modals';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {ExperimentFlowchart, prepareInitialExperimentalInputs} from '../design-state/ExperimentalInputs';
import {PlanConditionsByMethodId} from '../design-state/planConditionState';
import {ConditionGroup} from '../design-state/useExperimentDesignState';
import EditProcedureModal, {
  EditProcedureModalMode,
  ProcedureChangeParams,
} from '../procedure-selection/EditProcedureModal';
import {preserveProcedureInputsOnVersionUpdate} from '../procedure-selection/preserveProcedureInputsOnVersionUpdate';
import {ProcedureVersionUpdateCheckResult} from '../procedure-selection/useProcedureVersionUpdateCheck';
import {planConditionsForPreservation} from './preservedPlanInputsSerializationUtils';

interface UsePlanProcedureActionsParams {
  buildPreservedConditionIdMappings: (
    planConditionsByMethodId: PlanConditionsByMethodId,
    oldFlowchart: ExperimentFlowchart,
    newFlowchart: ExperimentFlowchart
  ) => Array<Record<string, string>>;
  conditions: Array<ConditionGroup>;
  experimentalInputs: ExperimentalInputs;
  flowchartVersionIdForNavigation: string;
  linkedFlowchartVersionId: string;
  procedureId: string;
  procedureName: string;
  procedureRunPlanId: string;
  procedureString: string;
  procedureType: ProcedureType;
  refetch: () => void;
  serializePreservedInputsForNetwork: (
    preservedInputs: ExperimentalInputs,
    planConditionsByMethodId: PlanConditionsByMethodId,
    methodIdsInOrder: ReadonlyArray<string>
  ) => Array<ProcedureRunPlanConditionREST>;
  studyId: string;
  studyType: StudyType;
  templateFlowchart: ExperimentFlowchart;
  versionUpdateCheck: ProcedureVersionUpdateCheckResult;
}

/**
 * Builds a uniform per-method map that gives every method the same rows from the combined-table
 * groups. Used for procedure and version changes, where surviving conditions are shared across all
 * methods in the destination flowchart.
 */
// TODO(BNCH-172699): Remove once plan state holds PlanConditionsByMethodId directly.
function uniformPlanConditionsForMethods(
  conditions: ReadonlyArray<ConditionGroup>,
  methodIds: ReadonlyArray<string>
): PlanConditionsByMethodId {
  const rows = conditions.map((condition) => ({
    name: condition.name,
    numReplicates: condition.numReplicates,
    ...(condition.description !== undefined ? {description: condition.description} : {}),
  }));
  return Object.fromEntries(methodIds.map((methodId) => [methodId, rows.map((row) => ({...row}))]));
}

interface UsePlanProcedureActionsResult {
  handleApplyProcedureUpdate: () => Promise<void>;
  handleChangeProcedure: (params: ProcedureChangeParams) => Promise<void>;
  handleOpenApplyUpdateModal: () => Promise<void>;
  handleRemoveProcedure: () => Promise<void>;
}

/**
 * Provides plan actions that replace, update, remove, or switch the selected procedure design.
 */
export default function usePlanProcedureActions({
  buildPreservedConditionIdMappings,
  conditions,
  experimentalInputs,
  flowchartVersionIdForNavigation,
  linkedFlowchartVersionId,
  procedureId,
  procedureName,
  procedureRunPlanId,
  procedureString,
  procedureType,
  refetch,
  serializePreservedInputsForNetwork,
  studyId,
  studyType,
  templateFlowchart,
  versionUpdateCheck,
}: UsePlanProcedureActionsParams): UsePlanProcedureActionsResult {
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);

  /**
   * Handles changing the procedure on a procedure run plan.
   * Updates the plan with the new flowchart version while preserving condition metadata
   * (names, descriptions, replicates) and resetting planned values to the selected procedure's defaults.
   */
  const handleChangeProcedure = useCallback(
    async (params: ProcedureChangeParams): Promise<void> => {
      const defaultInputsForNewProcedure = prepareInitialExperimentalInputs(
        conditions.length,
        params.procedure.methods
      );
      const newMethodIdsInOrder = Object.keys(defaultInputsForNewProcedure);
      const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(
        defaultInputsForNewProcedure
      );
      const conditionsForUpdate = serializePreservedInputsForNetwork(
        defaultInputsForNewProcedure,
        uniformPlanConditionsForMethods(conditions, newMethodIdsInOrder),
        newMethodIdsInOrder
      ).map((condition) => ({
        ...condition,
        // Unit operation links are specific to the old procedure and must be cleared.
        replicateInfos: condition.replicateInfos.map((replicateInfo) => ({
          ...replicateInfo,
          sourcePMEIIds: [],
        })),
      }));

      await updateProcedureRunPlan({
        id: procedureRunPlanId,
        studyId,
        // XOR validation handled by EditProcedureModal - only one of these will be set
        templateProcedureFlowchartVersionId: params.templateProcedureFlowchartVersionId,
        workflowFlowchartConfigVersionId: params.workflowFlowchartConfigVersionId,
        experimentConditions: conditionsForUpdate,
        confirmationMethodByParamVersionId,
      });

      refetch();
    },
    [conditions, procedureRunPlanId, refetch, serializePreservedInputsForNetwork, studyId]
  );

  /**
   * Compares the current and new procedure versions and preserves inputs and confirmation methods for
   * parameters/materials/equipment whose underlying procedure elements have
   * matching stable parameter IDs (as determined by preserveProcedureInputsOnVersionUpdate).
   */
  const handleApplyProcedureUpdate = useCallback(async (): Promise<void> => {
    if (
      isApplyingUpdate ||
      !versionUpdateCheck.hasNewerVersion ||
      !versionUpdateCheck.latestPublishedVersion
    ) {
      return;
    }

    setIsApplyingUpdate(true);

    try {
      const newFlowchart = versionUpdateCheck.latestPublishedVersion;
      const preservedInputs = preserveProcedureInputsOnVersionUpdate(
        experimentalInputs,
        templateFlowchart,
        newFlowchart,
        conditions.length
      );
      const newMethodIdsInOrder = Object.keys(preservedInputs);
      const oldMethodIdsInOrder = templateFlowchart.nodes.map((method) => method.id);
      const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(preservedInputs);
      const preservedConditionIdMappings = buildPreservedConditionIdMappings(
        planConditionsForPreservation(conditions, oldMethodIdsInOrder),
        templateFlowchart,
        newFlowchart
      );
      const conditionsForUpdate = serializePreservedInputsForNetwork(
        preservedInputs,
        uniformPlanConditionsForMethods(conditions, newMethodIdsInOrder),
        newMethodIdsInOrder
      ).map((condition) => {
        assert(
          condition.templateMethodVersionId != null,
          'Expected templateMethodVersionId for version update condition row'
        );
        const templateMethodVersionId = condition.templateMethodVersionId;
        const preservedConditionId =
          preservedConditionIdMappings[condition.position]?.[templateMethodVersionId];
        return {
          ...condition,
          id: preservedConditionId ?? undefined,
          // Keep existing unit operation links for rows that keep the same condition id.
          // For newly created rows, links reference old PMDVs and must be cleared.
          replicateInfos: condition.replicateInfos.map((replicateInfo) =>
            preservedConditionId != null
              ? replicateInfo
              : {
                  ...replicateInfo,
                  sourcePMEIIds: [],
                }
          ),
        };
      });

      await withLoadingScreen(
        updateProcedureRunPlan({
          id: procedureRunPlanId,
          studyId,
          templateProcedureFlowchartVersionId: versionUpdateCheck.isPoW ? undefined : newFlowchart.id,
          workflowFlowchartConfigVersionId: versionUpdateCheck.isPoW ? newFlowchart.id : undefined,
          experimentConditions: conditionsForUpdate,
          confirmationMethodByParamVersionId,
        }),
        `Updating to latest ${procedureString} version...`
      );

      notifications.success(`Updated to latest ${procedureString} version.`);
      refetch();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      if (net.isServerRequestError(error)) {
        notifications.error(
          <ToastMessage
            header={`Failed to apply ${procedureString} update`}
            body={notifications.getFriendlyErrorAsElement(error as Response, '')}
          />
        );
        return;
      }
      notifications.error(`Failed to apply ${procedureString} update. Please try again.`);
    } finally {
      setIsApplyingUpdate(false);
    }
  }, [
    buildPreservedConditionIdMappings,
    conditions,
    experimentalInputs,
    isApplyingUpdate,
    procedureRunPlanId,
    procedureString,
    refetch,
    serializePreservedInputsForNetwork,
    studyId,
    templateFlowchart,
    versionUpdateCheck,
  ]);

  const handleOpenApplyUpdateModal = useCallback(async (): Promise<void> => {
    if (!versionUpdateCheck.hasNewerVersion || !versionUpdateCheck.latestPublishedVersion) {
      return;
    }

    await legacyRunModal(EditProcedureModal, {
      mode: EditProcedureModalMode.VERSION_UPDATE,
      procedureId,
      flowchartVersionId: flowchartVersionIdForNavigation,
      outdatedFlowchartVersionId: flowchartVersionIdForNavigation,
      currentFlowchartVersionId: versionUpdateCheck.latestPublishedVersion.id,
      studyType,
      procedureType,
      onApplyVersionUpdate: handleApplyProcedureUpdate,
    });
  }, [
    flowchartVersionIdForNavigation,
    handleApplyProcedureUpdate,
    procedureId,
    procedureType,
    studyType,
    versionUpdateCheck.hasNewerVersion,
    versionUpdateCheck.latestPublishedVersion,
  ]);

  const handleRemoveProcedure = useCallback(async (): Promise<void> => {
    const shouldRemove = (await Promise.resolve(
      modals.confirm(
        `Remove ${procedureString}?`,
        <>
          Removing <strong>{procedureName}</strong> will clear all conditions and factor settings from this
          run plan.
        </>,
        ButtonStyle.NEGATIVE,
        {
          acceptText: 'Remove',
          rejectText: 'Cancel',
          enableRedesign: true,
          promiseCompatible: true,
        }
      )
    )) as boolean;

    if (!shouldRemove) {
      return;
    }

    const clearLinkedProcedureParams = isProcedureVersionPoW(linkedFlowchartVersionId)
      ? {workflowFlowchartConfigVersionId: null}
      : {templateProcedureFlowchartVersionId: null};

    await withLoadingScreen(
      updateProcedureRunPlan({
        id: procedureRunPlanId,
        studyId,
        ...clearLinkedProcedureParams,
        experimentConditions: [],
        confirmationMethodByParamVersionId: {},
      }),
      `Removing ${procedureString} from plan...`
    );

    refetch();
  }, [linkedFlowchartVersionId, procedureName, procedureRunPlanId, procedureString, refetch, studyId]);

  return {
    handleApplyProcedureUpdate,
    handleChangeProcedure,
    handleOpenApplyUpdateModal,
    handleRemoveProcedure,
  };
}
